import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DraggableToolbar from './DraggableToolbar';
import { smoothScrollTo, handleAppUrlClick, normalizeAppUrl as defaultNormalizeAppUrl, isFileUrl, isExternalOrFileUrl, resolveMediaUrl } from '../utils/styleHelpers.js';

export const EditableButton = ({
    button,
    isEditing,
    onChange,
    onDelete,
    onApplyToAll,
    btnStyleBase = {},
    getBtnColor = (variant) => {
        if (variant === 'secondary') return '#10b981';
        if (variant === 'tertiary') return '#8b5cf6';
        if (variant === 'white') return '#ffffff';
        if (variant === 'outline') return '#3b82f6';
        return '#3b82f6';
    },
    normalizeAppUrl = defaultNormalizeAppUrl,
    buttonsAlign,
    buttonsDirection = 'row',
    onButtonsAlignChange,
    style = {},
    className = ''
}) => {
    const [showToolbar, setShowToolbar] = useState(false);
    const [activeTab, setActiveTab] = useState('content');
    const [appliedToast, setAppliedToast] = useState(false);
    const [instanceId] = useState(() => Math.random().toString(36).substring(2, 9));

    // Ensure strictly ONE button CSS panel is open across the app at any given time
    useEffect(() => {
        const handleCloseOthers = (e) => {
            if (e.detail?.id !== instanceId) {
                setShowToolbar(false);
            }
        };
        window.addEventListener('close_other_button_toolbars', handleCloseOthers);
        return () => window.removeEventListener('close_other_button_toolbars', handleCloseOthers);
    }, [instanceId]);

    if (!button && !isEditing) return null;

    // Helper to format unit values
    const addUnit = (val) => {
        if (!val && val !== 0) return '';
        const str = String(val).trim();
        if (str === '0') return '0px';
        if (['px', 'rem', 'em', '%', 'vw'].some(u => str.endsWith(u))) return str;
        return `${str}px`;
    };

    // Helper to resolve button variant string
    const getVariant = (b) => {
        if (!b) return 'primary';
        if (b.variant) return b.variant;
        if (typeof b.style === 'string') return b.style;
        return 'primary';
    };

    // Helper to extract computed background color
    const getBgVal = (b) => {
        if (!b) return '#3b82f6';
        if (b.backgroundColor !== undefined && b.backgroundColor !== null && b.backgroundColor !== '') return b.backgroundColor;
        if (b.bgColor || b.bg) return b.bgColor || b.bg;
        const variant = getVariant(b);
        if (variant === 'outline') return 'transparent';
        return getBtnColor(variant);
    };

    // Helper to extract computed text color
    const getColorVal = (b) => {
        if (!b) return btnStyleBase?.color || '#ffffff';
        if (b.textColor || b.color) return b.textColor || b.color;
        const variant = getVariant(b);
        if (variant === 'white') return '#333333';
        if (variant === 'outline') return getBtnColor(variant || 'primary');
        if (btnStyleBase?.color) return btnStyleBase.color;
        return '#ffffff';
    };

    const effectiveAlign = button?.align || button?.btnAlign || (buttonsAlign && buttonsAlign !== 'inherit' ? buttonsAlign : undefined) || 'left';
    const isColumn = buttonsDirection === 'column' || buttonsDirection === 'vertical';
    const isFullWidth = effectiveAlign === 'full' || button?.width === '100%' || button?.width === 'full';
    const isRow = buttonsDirection === 'row';
    const wrapperWidth = (isFullWidth || !isRow) ? '100%' : 'auto';
    const wrapperDisplay = 'flex';

    // Build complete CSS style object for button
    const getComputedBtnStyle = (b) => {
        if (!b) return {};
        const bgVal = getBgVal(b);
        const colorVal = getColorVal(b);
        const variant = getVariant(b);

        let borderVal;
        if (b.border && b.border !== 'none') {
            borderVal = b.border;
        } else if (b.borderWidth && b.borderWidth !== '0px' && b.borderWidth !== '0') {
            borderVal = `${addUnit(b.borderWidth)} ${b.borderStyle || 'solid'} ${b.borderColor || 'currentColor'}`;
        } else if (variant === 'outline') {
            borderVal = `2px solid ${getBtnColor(variant)}`;
        } else if (b.borderWidth === '0px' || b.borderWidth === '0' || b.borderStyle === 'none') {
            borderVal = 'none';
        }

        const safeBaseStyle = (typeof btnStyleBase === 'object' && btnStyleBase !== null) ? btnStyleBase : {};
        const safeCustomStyle = (typeof b.style === 'object' && b.style !== null) ? b.style : {};
        const safePassedStyle = (typeof style === 'object' && style !== null) ? style : {};

        return {
            display: isFullWidth ? 'block' : 'inline-block',
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: 'inherit',
            fontWeight: 'bold',
            textTransform: 'none',
            letterSpacing: 'normal',
            width: isFullWidth ? '100%' : 'auto',
            textAlign: 'center',
            boxShadow: 'none',
            textDecoration: 'none',
            transition: 'all 0.25s ease',
            cursor: 'pointer',
            boxSizing: 'border-box',
            ...safeBaseStyle,
            background: bgVal,
            color: colorVal,
            ...(b.padding ? { padding: b.padding } : {}),
            ...(b.borderRadius ? { borderRadius: b.borderRadius } : {}),
            ...(b.fontSize ? { fontSize: b.fontSize } : {}),
            ...(b.fontWeight ? { fontWeight: b.fontWeight } : {}),
            ...(b.textTransform ? { textTransform: b.textTransform } : {}),
            ...(b.letterSpacing ? { letterSpacing: b.letterSpacing } : {}),
            ...(b.width ? { width: b.width } : {}),
            ...(b.boxShadow ? { boxShadow: b.boxShadow } : {}),
            ...(b.transition ? { transition: b.transition } : {}),
            ...(borderVal !== undefined ? { border: borderVal } : {}),
            ...safeCustomStyle,
            ...safePassedStyle
        };
    };

    // Handle mouse enter for hover effects
    const handleMouseEnter = (e) => {
        if (!button) return;
        const hoverBg = button.hoverBackgroundColor || button.hoverBg || btnStyleBase?.buttonHoverBg;
        if (hoverBg) {
            e.currentTarget.style.background = hoverBg;
        }
        const hoverText = button.hoverTextColor || btnStyleBase?.buttonHoverTextColor;
        if (hoverText) {
            e.currentTarget.style.color = hoverText;
        }
        if (button.hoverEffect === 'scale') e.currentTarget.style.transform = 'scale(1.05)';
        if (button.hoverEffect === 'lift') e.currentTarget.style.transform = 'translateY(-3px)';
        if (button.hoverEffect === 'glow') {
            const glowCol = button.glowColor || button.hoverGlowColor || button.borderColor || '#3b82f6';
            e.currentTarget.style.boxShadow = `0 0 18px ${glowCol}`;
        }
        if (button.hoverEffect === 'fade') e.currentTarget.style.opacity = '0.85';
    };

    // Handle mouse leave
    const handleMouseLeave = (e) => {
        if (!button) return;
        const bStyle = getComputedBtnStyle(button);
        e.currentTarget.style.background = bStyle.background;
        e.currentTarget.style.color = bStyle.color;
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = bStyle.boxShadow;
        e.currentTarget.style.opacity = '1';
    };

    // Normal view (not editing)
    if (!isEditing) {
        if (!button) return null;
        const bStyle = getComputedBtnStyle(button);
        const isFile = isFileUrl(button?.link);
        const normalizedLink = isFile ? resolveMediaUrl(button?.link) : normalizeAppUrl(button?.link);

        const handleBtnClick = (e) => {
            handleAppUrlClick(e, button?.link, false);
        };

        const isExternal = isFile || isExternalOrFileUrl(button?.link) || isExternalOrFileUrl(normalizedLink);
        const btnElem = isExternal ? (
            <a
                href={normalizedLink}
                target={button.target || (isFile ? "_blank" : "_blank")}
                rel="noreferrer"
                style={bStyle}
                className={className}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleBtnClick}
            >
                {button.text}
            </a>
        ) : (
            <Link
                to={normalizedLink}
                style={bStyle}
                className={className}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleBtnClick}
            >
                {button.text}
            </Link>
        );

        const justifyPos = effectiveAlign === 'center' ? 'center' : effectiveAlign === 'right' ? 'flex-end' : effectiveAlign === 'full' ? 'stretch' : 'flex-start';

        return (
            <div style={{ display: wrapperDisplay, justifyContent: justifyPos, width: wrapperWidth, margin: isFullWidth ? '4px 0' : '0' }}>
                {btnElem}
            </div>
        );
    }

    // Editing mode:
    // If no button exists yet, render an "+ Add Button" trigger
    if (!button) {
        return (
            <div style={{ position: 'relative', height: 0, overflow: 'visible' }}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onChange({ text: 'Learn More', link: '#', variant: 'primary' });
                        setShowToolbar(true);
                    }}
                    style={{
                        position: 'absolute',
                        top: '4px',
                        left: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '5px 12px',
                        background: '#e0e7ff',
                        color: '#3730a3',
                        border: '1px dashed #6366f1',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                        ...style
                    }}
                >
                    ➕ Add Button
                </button>
            </div>
        );
    }

    const toggleToolbar = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!showToolbar) {
            window.dispatchEvent(new CustomEvent('close_other_button_toolbars', { detail: { id: instanceId } }));
        }
        setShowToolbar(prev => !prev);
    };

    const bStyle = getComputedBtnStyle(button);
    const justifyPos = effectiveAlign === 'center' ? 'center' : effectiveAlign === 'right' ? 'flex-end' : effectiveAlign === 'full' ? 'stretch' : 'flex-start';

    return (
        <div style={{ display: wrapperDisplay, justifyContent: justifyPos, width: wrapperWidth, margin: isFullWidth ? '4px 0' : '0' }}>
            <div
                style={{
                    position: 'relative',
                    display: isFullWidth ? 'block' : 'inline-block',
                    width: isFullWidth ? '100%' : 'auto',
                    padding: '4px',
                    borderRadius: bStyle.borderRadius ? `calc(${bStyle.borderRadius} + 4px)` : '8px',
                    border: showToolbar ? '2px solid #3b82f6' : '1px dashed rgba(59, 130, 246, 0.4)',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease'
                }}
                onClick={e => e.stopPropagation()}
            >
            <div
                onClick={toggleToolbar}
                style={{
                    ...bStyle,
                    position: 'relative',
                    cursor: 'pointer'
                }}
                title="Click to edit button text, link, and full CSS styles"
            >
                <span>{button.text || 'Button Text'}</span>
                <span
                    style={{
                        marginLeft: '8px',
                        fontSize: '10px',
                        background: 'rgba(0,0,0,0.2)',
                        padding: '2px 5px',
                        borderRadius: '3px',
                        verticalAlign: 'middle'
                    }}
                >
                    ✏️
                </span>
            </div>

            {showToolbar && (
                <DraggableToolbar
                    title="🔘 Button Options & CSS Styling"
                    onClose={() => setShowToolbar(false)}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            width: '100%',
                            maxWidth: '340px',
                            padding: '10px',
                            color: '#334155',
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            boxSizing: 'border-box'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* CSS Category Navigation Tabs */}
                        <div style={{ display: 'flex', borderBottom: '1px solid #cbd5e1', gap: '4px', paddingBottom: '4px', flexWrap: 'wrap' }}>
                            {[
                                { id: 'content', label: '📝 Link' },
                                { id: 'colors', label: '🎨 Colours' },
                                { id: 'spacing', label: '📐 Size & Radius' },
                                { id: 'font', label: '🔤 Font & Effects' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    style={{
                                        flex: 1,
                                        padding: '5px 4px',
                                        fontSize: '10px',
                                        fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                                        background: activeTab === tab.id ? '#3b82f6' : '#f1f5f9',
                                        color: activeTab === tab.id ? '#ffffff' : '#475569',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* TAB 1: Content & Link */}
                        {activeTab === 'content' && (
                            <>
                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Button Text
                                    </label>
                                    <input
                                        type="text"
                                        value={button.text || ''}
                                        onChange={e => onChange({ ...button, text: e.target.value })}
                                        placeholder="Click Here"
                                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Link / URL
                                    </label>
                                    <input
                                        type="text"
                                        value={button.link || ''}
                                        onChange={e => onChange({ ...button, link: e.target.value })}
                                        placeholder="e.g. /shop, #section, https://..."
                                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Preset Variant
                                    </label>
                                    <select
                                        value={button.variant || 'primary'}
                                        onChange={e => onChange({ ...button, variant: e.target.value })}
                                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', background: 'white' }}
                                    >
                                        <option value="primary">Primary Colour</option>
                                        <option value="secondary">Secondary Colour</option>
                                        <option value="tertiary">Tertiary Colour</option>
                                        <option value="white">White Background</option>
                                        <option value="outline">Outline Only</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        🧭 Button Alignment
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
                                        {[
                                            { label: 'Left ⬅️', val: 'left' },
                                            { label: 'Center ↔️', val: 'center' },
                                            { label: 'Right ➡️', val: 'right' },
                                            { label: 'Full ↔', val: 'full' }
                                        ].map(opt => {
                                            const isSel = effectiveAlign === opt.val;
                                            return (
                                                <button
                                                    key={opt.val}
                                                    type="button"
                                                    onClick={() => {
                                                        if (onButtonsAlignChange) onButtonsAlignChange(opt.val);
                                                        onChange({
                                                            ...button,
                                                            align: opt.val,
                                                            btnAlign: opt.val,
                                                            width: opt.val === 'full' ? '100%' : (button.width === '100%' ? 'auto' : (button.width || 'auto'))
                                                        });
                                                    }}
                                                    style={{
                                                        padding: '5px 2px',
                                                        fontSize: '10px',
                                                        fontWeight: isSel ? 'bold' : 'normal',
                                                        background: isSel ? '#3b82f6' : '#ffffff',
                                                        color: isSel ? '#ffffff' : '#334155',
                                                        border: `1px solid ${isSel ? '#2563eb' : '#cbd5e1'}`,
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input
                                        type="checkbox"
                                        id="btn-target-blank"
                                        checked={button.target === '_blank'}
                                        onChange={e => onChange({ ...button, target: e.target.checked ? '_blank' : '_self' })}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <label htmlFor="btn-target-blank" style={{ fontSize: '11px', color: '#475569', cursor: 'pointer' }}>
                                        Open link in new tab
                                    </label>
                                </div>
                            </>
                        )}

                        {/* TAB 2: Colours & Custom Background */}
                        {activeTab === 'colors' && (
                            <>
                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Background Colour (CSS `background`)
                                    </label>

                                    {/* Quick Mode Presets: Transparent, Glass RGBA, Dark RGBA */}
                                    <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                                        <button
                                            type="button"
                                            onClick={() => onChange({ ...button, backgroundColor: 'transparent' })}
                                            style={{
                                                flex: 1,
                                                padding: '4px 6px',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                background: (button.backgroundColor === 'transparent' || getBgVal(button) === 'transparent') ? '#3b82f6' : '#e2e8f0',
                                                color: (button.backgroundColor === 'transparent' || getBgVal(button) === 'transparent') ? '#ffffff' : '#334155',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🚫 Transparent
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onChange({ ...button, backgroundColor: 'rgba(255, 255, 255, 0.2)' })}
                                            style={{
                                                flex: 1,
                                                padding: '4px 6px',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                background: (button.backgroundColor && button.backgroundColor.includes('rgba')) ? '#3b82f6' : '#e2e8f0',
                                                color: (button.backgroundColor && button.backgroundColor.includes('rgba')) ? '#ffffff' : '#334155',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ✨ Light RGBA
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onChange({ ...button, backgroundColor: 'rgba(15, 23, 42, 0.75)' })}
                                            style={{
                                                flex: 1,
                                                padding: '4px 6px',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                background: (button.backgroundColor && button.backgroundColor.includes('15, 23')) ? '#3b82f6' : '#e2e8f0',
                                                color: (button.backgroundColor && button.backgroundColor.includes('15, 23')) ? '#ffffff' : '#334155',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🌙 Dark RGBA
                                        </button>
                                    </div>

                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <input
                                            type="color"
                                            value={getBgVal(button).startsWith('#') ? getBgVal(button) : '#3b82f6'}
                                            onChange={e => onChange({ ...button, backgroundColor: e.target.value })}
                                            style={{ width: '36px', height: '30px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={button.backgroundColor !== undefined ? button.backgroundColor : (button.bgColor || button.bg || '')}
                                            onChange={e => onChange({ ...button, backgroundColor: e.target.value })}
                                            placeholder="e.g. transparent, rgba(255,255,255,0.5), #3b82f6"
                                            style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Text Colour (CSS `color`)
                                    </label>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <input
                                            type="color"
                                            value={getColorVal(button).startsWith('#') ? getColorVal(button) : '#ffffff'}
                                            onChange={e => onChange({ ...button, textColor: e.target.value })}
                                            style={{ width: '36px', height: '30px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={button.textColor || button.color || ''}
                                            onChange={e => onChange({ ...button, textColor: e.target.value })}
                                            placeholder="e.g. #ffffff"
                                            style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Hover Background Colour
                                    </label>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <input
                                            type="color"
                                            value={button.hoverBackgroundColor && button.hoverBackgroundColor.startsWith('#') ? button.hoverBackgroundColor : '#2563eb'}
                                            onChange={e => onChange({ ...button, hoverBackgroundColor: e.target.value })}
                                            style={{ width: '36px', height: '30px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={button.hoverBackgroundColor || button.hoverBg || ''}
                                            onChange={e => onChange({ ...button, hoverBackgroundColor: e.target.value })}
                                            placeholder="Hover Background (e.g. #2563eb)"
                                            style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Hover Text Colour
                                    </label>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <input
                                            type="color"
                                            value={button.hoverTextColor && button.hoverTextColor.startsWith('#') ? button.hoverTextColor : '#ffffff'}
                                            onChange={e => onChange({ ...button, hoverTextColor: e.target.value })}
                                            style={{ width: '36px', height: '30px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={button.hoverTextColor || ''}
                                            onChange={e => onChange({ ...button, hoverTextColor: e.target.value })}
                                            placeholder="Hover Text Colour"
                                            style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '4px' }}>
                                        Outer Glow Colour
                                    </label>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <input
                                            type="color"
                                            value={button.glowColor && button.glowColor.startsWith('#') ? button.glowColor : '#3b82f6'}
                                            onChange={e => onChange({ ...button, glowColor: e.target.value })}
                                            style={{ width: '36px', height: '30px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={button.glowColor || ''}
                                            onChange={e => onChange({ ...button, glowColor: e.target.value })}
                                            placeholder="Outer Glow Colour (e.g. #3b82f6 or rgba(...))"
                                            style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* TAB 3: Geometry, Spacing & Borders */}
                        {activeTab === 'spacing' && (
                            <>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Corner Radius (`border-radius`)
                                        </label>
                                        <input
                                            type="text"
                                            value={button.borderRadius || ''}
                                            onChange={e => onChange({ ...button, borderRadius: e.target.value })}
                                            placeholder="6px (30px for pill)"
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Padding (`padding`)
                                        </label>
                                        <input
                                            type="text"
                                            value={button.padding || ''}
                                            onChange={e => onChange({ ...button, padding: e.target.value })}
                                            placeholder="10px 20px"
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Width (`width`)
                                        </label>
                                        <select
                                            value={button.width || 'auto'}
                                            onChange={e => onChange({ ...button, width: e.target.value })}
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px', background: 'white' }}
                                        >
                                            <option value="auto">Auto (Fits Text)</option>
                                            <option value="100%">Full Width (100%)</option>
                                            <option value="200px">200px</option>
                                            <option value="250px">250px</option>
                                            <option value="300px">300px</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Border Width
                                        </label>
                                        <input
                                            type="text"
                                            value={button.borderWidth || ''}
                                            onChange={e => onChange({ ...button, borderWidth: e.target.value })}
                                            placeholder="0px or 2px"
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Border Style
                                        </label>
                                        <select
                                            value={button.borderStyle || 'solid'}
                                            onChange={e => onChange({ ...button, borderStyle: e.target.value })}
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px', background: 'white' }}
                                        >
                                            <option value="solid">Solid</option>
                                            <option value="dashed">Dashed</option>
                                            <option value="dotted">Dotted</option>
                                            <option value="none">None</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Border Colour
                                        </label>
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            <input
                                                type="color"
                                                value={button.borderColor && button.borderColor.startsWith('#') ? button.borderColor : '#cbd5e1'}
                                                onChange={e => onChange({ ...button, borderColor: e.target.value })}
                                                style={{ width: '32px', height: '28px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                            />
                                            <input
                                                type="text"
                                                value={button.borderColor || ''}
                                                onChange={e => onChange({ ...button, borderColor: e.target.value })}
                                                placeholder="#cbd5e1 or rgba(...)"
                                                style={{ flex: 1, padding: '5px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* TAB 4: Typography, Shadow & Hover Effect */}
                        {activeTab === 'font' && (
                            <>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Font Size (`font-size`)
                                        </label>
                                        <input
                                            type="text"
                                            value={button.fontSize || ''}
                                            onChange={e => onChange({ ...button, fontSize: e.target.value })}
                                            placeholder="1rem or 14px"
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Font Weight (`font-weight`)
                                        </label>
                                        <select
                                            value={button.fontWeight || 'bold'}
                                            onChange={e => onChange({ ...button, fontWeight: e.target.value })}
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px', background: 'white' }}
                                        >
                                            <option value="normal">Normal (400)</option>
                                            <option value="500">Medium (500)</option>
                                            <option value="bold">Bold (700)</option>
                                            <option value="800">Extra Bold (800)</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Text Transform
                                        </label>
                                        <select
                                            value={button.textTransform || 'none'}
                                            onChange={e => onChange({ ...button, textTransform: e.target.value })}
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px', background: 'white' }}
                                        >
                                            <option value="none">Normal</option>
                                            <option value="uppercase">UPPERCASE</option>
                                            <option value="lowercase">lowercase</option>
                                            <option value="capitalize">Capitalize</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Letter Spacing
                                        </label>
                                        <input
                                            type="text"
                                            value={button.letterSpacing || ''}
                                            onChange={e => onChange({ ...button, letterSpacing: e.target.value })}
                                            placeholder="normal or 0.05em"
                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                        Box Shadow (`box-shadow`)
                                    </label>
                                    <select
                                        value={button.boxShadow || 'none'}
                                        onChange={e => onChange({ ...button, boxShadow: e.target.value })}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px', background: 'white' }}
                                    >
                                        <option value="none">None</option>
                                        <option value="0 4px 6px -1px rgba(0,0,0,0.1)">Subtle Shadow</option>
                                        <option value="0 10px 15px -3px rgba(0,0,0,0.15)">Standard Shadow</option>
                                        <option value="0 20px 25px -5px rgba(0,0,0,0.2)">Heavy Shadow</option>
                                        <option value="0 0 15px rgba(59,130,246,0.5)">Blue Glow</option>
                                        <option value="0 0 15px rgba(16,185,129,0.5)">Green Glow</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                        Hover Effect & Animation
                                    </label>
                                    <select
                                        value={button.hoverEffect || 'none'}
                                        onChange={e => onChange({ ...button, hoverEffect: e.target.value })}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px', background: 'white' }}
                                    >
                                        <option value="none">None</option>
                                        <option value="scale">Scale Up (1.05x)</option>
                                        <option value="lift">Lift Up (-3px)</option>
                                        <option value="glow">Outer Glow</option>
                                        <option value="fade">Fade Opacity (85%)</option>
                                    </select>
                                </div>

                                {(button.hoverEffect === 'glow' || button.glowColor) && (
                                    <div>
                                        <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                                            Outer Glow Colour
                                        </label>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <input
                                                type="color"
                                                value={button.glowColor && button.glowColor.startsWith('#') ? button.glowColor : '#3b82f6'}
                                                onChange={e => onChange({ ...button, glowColor: e.target.value })}
                                                style={{ width: '32px', height: '28px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                            />
                                            <input
                                                type="text"
                                                value={button.glowColor || ''}
                                                onChange={e => onChange({ ...button, glowColor: e.target.value })}
                                                placeholder="#3b82f6 or rgba(...)"
                                                style={{ flex: 1, padding: '5px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {onApplyToAll && (
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onApplyToAll(button);
                                        setAppliedToast(true);
                                        setTimeout(() => setAppliedToast(false), 2500);
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        background: appliedToast ? '#16a34a' : '#2563eb',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.25)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    title="Copy this button's background, colors, borders, radius, padding, and effects to all Hero buttons"
                                >
                                    {appliedToast ? '✓ Applied to All Hero Buttons!' : '⚡ Apply Style to All Hero Buttons'}
                                </button>
                            </div>
                        )}

                        {onDelete && (
                            <button
                                type="button"
                                onClick={() => {
                                    onDelete();
                                    setShowToolbar(false);
                                }}
                                style={{
                                    marginTop: '8px',
                                    padding: '7px 12px',
                                    background: '#fee2e2',
                                    color: '#ef4444',
                                    border: '1px solid #fca5a5',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                🗑️ Remove Button
                            </button>
                        )}
                    </div>
                </DraggableToolbar>
            )}
        </div>
    </div>
);
};

export default EditableButton;

